import React, { useState } from 'react';
import { X, Trash2, GripVertical, Plus, Check } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RYAN_FIELD_COLORS, RYAN_SUBDECK_COLORS, getOrderedFieldColorFamilies } from '../constants/ryanFieldVisuals';
import { RyanDisplayCategoryData } from '../types/ryan.types';

interface RyanManageDisplayCategoriesModalProps {
  spaceId: string;
  /** Cor do próprio campo — usada só pra destacar a linha correspondente no
      seletor de cor (mesmo "hover permanente" já usado no editor do subdeck). */
  fieldColorId?: string;
  categories: RyanDisplayCategoryData[];
  onClose: () => void;
  onChange: (categories: RyanDisplayCategoryData[]) => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const SortableCategoryRow: React.FC<{
  category: RyanDisplayCategoryData;
  fieldColorId?: string;
  isEditing: boolean;
  draftName: string;
  onStartEdit: () => void;
  onChangeDraftName: (v: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onChangeColor: (color: string) => void;
  onChangeColumns: (columns: 2 | 3) => void;
  onDelete: () => void;
  showColorPicker: boolean;
  onToggleColorPicker: () => void;
}> = ({
  category, fieldColorId, isEditing, draftName, onStartEdit, onChangeDraftName, onSaveEdit, onCancelEdit,
  onChangeColor, onChangeColumns, onDelete, showColorPicker, onToggleColorPicker,
}) => {
  const columns = category.gridColumns || 3;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category._id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
        background: '#f8faf8', borderRadius: 10, border: '1px solid rgba(13,59,14,0.08)',
      }}>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--text-secondary)', display: 'flex', flexShrink: 0 }}>
          <GripVertical size={15} />
        </div>

        <button
          type="button"
          onClick={onToggleColorPicker}
          style={{
            width: 22, height: 22, borderRadius: '50%', background: category.color,
            border: '2px solid rgba(0,0,0,0.1)', cursor: 'pointer', flexShrink: 0, padding: 0,
          }}
          title="Trocar cor"
        />

        {isEditing ? (
          <input
            className="ryan-modal-input"
            style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
            value={draftName}
            onChange={e => onChangeDraftName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSaveEdit()}
            autoFocus
          />
        ) : (
          <span
            style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}
            onClick={onStartEdit}
          >
            {category.name}
            {category.isDefault && (
              <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                padrão
              </span>
            )}
          </span>
        )}

        {isEditing ? (
          <>
            <button className="ryan-group-kebab-btn" onClick={onSaveEdit} title="Salvar">
              <Check size={15} />
            </button>
            <button className="ryan-group-kebab-btn" onClick={onCancelEdit} title="Cancelar">
              <X size={15} />
            </button>
          </>
        ) : (
          <>
            {/* Colunas do grid desta seção no computador (padrão 3) */}
            <div
              title="Colunas desta seção no computador (em telas menores segue o responsivo)"
              style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
            >
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>Colunas</span>
              {([2, 3] as const).map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => n !== columns && onChangeColumns(n)}
                  style={{
                    width: 26, height: 24, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', padding: 0,
                    border: `1.5px solid ${columns === n ? '#0D3B0E' : 'rgba(13,59,14,0.15)'}`,
                    background: columns === n ? 'rgba(13,59,14,0.08)' : 'white',
                    color: columns === n ? '#0D3B0E' : 'var(--text-secondary)',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            {!category.isDefault && (
              <button className="ryan-group-kebab-btn" onClick={onDelete} title="Excluir categoria" style={{ color: '#dc2626' }}>
                <Trash2 size={15} />
              </button>
            )}
          </>
        )}
      </div>

      {showColorPicker && (
        <div style={{ margin: '8px 12px 4px 40px' }} className="ryan-subdeck-color-groups">
          {getOrderedFieldColorFamilies(fieldColorId).map(family => {
            const isFieldColor = family.id === (fieldColorId || 'floresta');
            return (
              <div
                key={family.id}
                className={`ryan-subdeck-color-group${isFieldColor ? ' ryan-subdeck-color-group--fieldmatch' : ''}`}
                style={isFieldColor ? ({ '--fm-color': family.color } as React.CSSProperties) : undefined}
              >
                <span className="ryan-subdeck-color-group-label">
                  {family.label}
                  {isFieldColor && <span className="ryan-subdeck-color-group-badge">cor deste campo</span>}
                </span>
                <div className="ryan-subdeck-color-group-swatches">
                  {RYAN_SUBDECK_COLORS.filter(c => c.familyId === family.id).map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className={`ryan-color-dot-btn${category.color === c.color ? ' selected' : ''}${c.isParent ? ' ryan-color-dot-btn--parent' : ''}`}
                      style={{ background: c.color }}
                      onClick={() => onChangeColor(c.color)}
                      title={c.isParent ? `${family.label} — cor do campo` : `${family.label} — ${c.tone}`}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RyanManageDisplayCategoriesModal: React.FC<RyanManageDisplayCategoriesModalProps> = ({
  spaceId, fieldColorId, categories, onClose, onChange, onToast,
}) => {
  const [items, setItems] = useState<RyanDisplayCategoryData[]>(
    () => [...categories].sort((a, b) => a.order - b.order)
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [colorPickerId, setColorPickerId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const token = () => localStorage.getItem('accessToken') || '';

  const applyChange = (next: RyanDisplayCategoryData[]) => {
    setItems(next);
    onChange(next);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex(c => c._id === active.id);
    const newIdx = items.findIndex(c => c._id === over.id);
    const reordered = arrayMove(items, oldIdx, newIdx);
    applyChange(reordered);

    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/display-categories/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ categories: reordered.map((c, i) => ({ _id: c._id, order: i })) }),
      });
      if (res.ok) applyChange(await res.json());
    } catch {
      onToast('Erro ao reordenar categorias', 'error');
    }
  };

  const startEdit = (category: RyanDisplayCategoryData) => {
    setEditingId(category._id);
    setDraftName(category.name);
    setColorPickerId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName('');
  };

  const saveEdit = async (categoryId: string) => {
    if (!draftName.trim()) { onToast('Nome obrigatório', 'error'); return; }
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/display-categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ name: draftName.trim() }),
      });
      if (!res.ok) { onToast('Erro ao renomear categoria', 'error'); return; }
      const updated = await res.json();
      applyChange(items.map(c => c._id === categoryId ? updated : c));
      setEditingId(null);
    } catch {
      onToast('Erro de conexão', 'error');
    }
  };

  const changeColor = async (categoryId: string, color: string) => {
    setColorPickerId(null);
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/display-categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ color }),
      });
      if (!res.ok) { onToast('Erro ao trocar cor', 'error'); return; }
      const updated = await res.json();
      applyChange(items.map(c => c._id === categoryId ? updated : c));
    } catch {
      onToast('Erro de conexão', 'error');
    }
  };

  const changeColumns = async (categoryId: string, gridColumns: 2 | 3) => {
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/display-categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ gridColumns }),
      });
      if (!res.ok) { onToast('Erro ao trocar colunas', 'error'); return; }
      const updated = await res.json();
      applyChange(items.map(c => c._id === categoryId ? updated : c));
    } catch {
      onToast('Erro de conexão', 'error');
    }
  };

  const deleteCategory = async (category: RyanDisplayCategoryData) => {
    if (!window.confirm(`Excluir categoria "${category.name}"? Decks nela voltam pra categoria padrão do seu tipo.`)) return;
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/display-categories/${category._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const e = await res.json();
        onToast(e.message || 'Erro ao excluir categoria', 'error');
        return;
      }
      applyChange(items.filter(c => c._id !== category._id));
      onToast('Categoria excluída!', 'success');
    } catch {
      onToast('Erro de conexão', 'error');
    }
  };

  const createCategory = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/display-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ name: newName.trim(), color: RYAN_FIELD_COLORS[items.length % RYAN_FIELD_COLORS.length].color }),
      });
      if (!res.ok) {
        const e = await res.json();
        onToast(e.message || 'Erro ao criar categoria', 'error');
        return;
      }
      const created = await res.json();
      applyChange([...items, created]);
      setNewName('');
    } catch {
      onToast('Erro de conexão', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal ryan-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">Gerenciar Categorias</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}>
            Arraste para reordenar as seções do modo "Por Tipo". Clique no nome pra renomear, na bolinha de cor pra trocar a cor. Em "Colunas", escolha quantos decks por linha a seção mostra no computador (padrão 3).
          </p>

          {items.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: '24px 0', fontFamily: 'Inter, sans-serif' }}>
              Nenhuma categoria ainda.
            </p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map(c => c._id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(category => (
                    <SortableCategoryRow
                      key={category._id}
                      category={category}
                      fieldColorId={fieldColorId}
                      isEditing={editingId === category._id}
                      draftName={draftName}
                      onStartEdit={() => startEdit(category)}
                      onChangeDraftName={setDraftName}
                      onSaveEdit={() => saveEdit(category._id)}
                      onCancelEdit={cancelEdit}
                      onChangeColor={color => changeColor(category._id, color)}
                      onChangeColumns={cols => changeColumns(category._id, cols)}
                      onDelete={() => deleteCategory(category)}
                      showColorPicker={colorPickerId === category._id}
                      onToggleColorPicker={() => setColorPickerId(prev => prev === category._id ? null : category._id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <input
              className="ryan-modal-input"
              placeholder="Nova categoria…"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createCategory()}
            />
            <button className="ryan-modal-save-btn" onClick={createCategory} disabled={creating || !newName.trim()}>
              <Plus size={14} /> Criar
            </button>
          </div>
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default RyanManageDisplayCategoriesModal;
